import React, { HTMLAttributes, ReactNode } from "react";

import { FieldError } from "react-hook-form";
import classnames from "classnames";

import { useWombatTheme } from "../../../theme";

export type CheckboxGroupDisplay = "col" | "row";

type CheckboxGroupProps = {
  /**
   * Title of the checkbox group
   */
  title?: ReactNode;
  /**
   * Description of the checkbox group
   */
  description?: ReactNode;
  /**
   * Display type
   * Note: To respect the style guidelines,
   * if display is set to 'row' checkboxes must be passed the 'boxed' props to true
   * @default 'col'
   */
  display?: CheckboxGroupDisplay;
  /**
   * Helper text, prefer to used this help text instead of individual ones
   * @default undefined
   */
  help?: string;
  /**
   * The react-hook-form error field
   * @default {}
   */
  error?: FieldError;
  /**
   * Disabled all descendant of the checkbox group
   * @default false
   */
  disabled?: boolean;
} & Omit<HTMLAttributes<HTMLFieldSetElement>, 'title'>;

/**
 * CheckboxGroup component is meant to receive Checkbox only children
 */
const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  title,
  description,
  disabled = false,
  display = "col",
  children,
  help,
  error = {},
  ...props
}) => {
  const { theme } = useWombatTheme();

  const titleStyle = classnames(theme.form.checkbox.group.title, 'w-full');
  const descriptionStyle = classnames(theme.form.checkbox.group.description);
  const containerStyle = classnames(theme.form.checkbox.group.layout[display]);
  const helpStyle = classnames(
    theme.form.checkbox.group.help[error?.message ? "error" : "default"]
  );
  return (
    <fieldset {...props} disabled={disabled}>
      <legend className={titleStyle}>{title}</legend>
      {description && <div className={descriptionStyle}>{description}</div>}
      <div className={containerStyle}>{children}</div>
      {(help || error?.message) && (
        <div className={helpStyle}>{error?.message ? error?.message : help}</div>
      )}
    </fieldset>
  );
};

export default React.memo(CheckboxGroup);
